import type { TokenPair } from './types';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface TokenStore {
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  setTokens(tokens: TokenPair): void;
  clear(): void;
}

const REFRESH_KEY = 'speakmanager.refresh_token';

export class MemoryTokenStore implements TokenStore {
  private access: string | null = null;

  constructor(private readonly storage: Storage | null = typeof sessionStorage === 'undefined' ? null : sessionStorage) {}

  getAccessToken() {
    return this.access;
  }

  getRefreshToken() {
    return this.storage?.getItem(REFRESH_KEY) ?? null;
  }

  setTokens(tokens: TokenPair) {
    this.access = tokens.access_token;
    if (tokens.refresh_token) this.storage?.setItem(REFRESH_KEY, tokens.refresh_token);
  }

  clear() {
    this.access = null;
    this.storage?.removeItem(REFRESH_KEY);
  }
}

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  body?: unknown;
  form?: FormData;
  query?: Query;
  auth?: boolean;
  raw?: boolean;
  signal?: AbortSignal;
}

export function unwrap<T>(body: unknown): T {
  if (body && typeof body === 'object' && (body as { status?: unknown }).status === 'success' && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

export function extractTokens(value: unknown): TokenPair {
  const v = value as { tokens?: TokenPair } & Partial<TokenPair>;
  const pair = v?.tokens ?? (v as TokenPair);
  if (!pair?.access_token) throw new ApiError(500, 'invalid_tokens', 'Response contains no tokens');
  return pair;
}

export function asList<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value && typeof value === 'object') {
    for (const key of ['items', 'results', 'events', 'sessions', 'voices', 'data']) {
      const inner = (value as Record<string, unknown>)[key];
      if (Array.isArray(inner)) return inner as T[];
    }
  }
  return [];
}

export function buildQuery(query?: Query): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

export class ApiClient {
  private refreshing: Promise<boolean> | null = null;
  onAuthLost: (() => void) | null = null;

  constructor(
    public readonly baseUrl: string,
    public readonly tokens: TokenStore,
    private readonly fetchImpl: typeof fetch = (...args) => fetch(...args),
  ) {}

  async request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.send(method, path, options);
    if (response.status === 401 && options.auth !== false && this.tokens.getRefreshToken()) {
      if (await this.refresh()) {
        return this.parse<T>(await this.send(method, path, options), options);
      }
    }
    return this.parse<T>(response, options);
  }

  get<T>(path: string, query?: Query) {
    return this.request<T>('GET', path, { query });
  }

  post<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'body'> = {}) {
    return this.request<T>('POST', path, { ...options, body });
  }

  put<T>(path: string, body?: unknown) {
    return this.request<T>('PUT', path, { body });
  }

  patch<T>(path: string, body?: unknown) {
    return this.request<T>('PATCH', path, { body });
  }

  delete<T>(path: string) {
    return this.request<T>('DELETE', path);
  }

  async refresh(): Promise<boolean> {
    if (!this.refreshing) {
      this.refreshing = (async () => {
        const refreshToken = this.tokens.getRefreshToken();
        if (!refreshToken) return false;
        try {
          const response = await this.send('POST', '/auth/refresh', {
            body: { refresh_token: refreshToken },
            auth: false,
          });
          if (!response.ok) {
            this.tokens.clear();
            this.onAuthLost?.();
            return false;
          }
          this.tokens.setTokens(extractTokens(unwrap(await response.json())));
          return true;
        } catch {
          return false;
        } finally {
          setTimeout(() => {
            this.refreshing = null;
          }, 0);
        }
      })();
    }
    return this.refreshing;
  }

  private send(method: string, path: string, options: RequestOptions): Promise<Response> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    let body: BodyInit | undefined;
    if (options.form) {
      body = options.form;
    } else if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(options.body);
    }
    const token = this.tokens.getAccessToken();
    if (token && options.auth !== false) headers.Authorization = `Bearer ${token}`;
    return this.fetchImpl(`${this.baseUrl}${path}${buildQuery(options.query)}`, {
      method,
      headers,
      body,
      signal: options.signal,
    });
  }

  private async parse<T>(response: Response, options: RequestOptions): Promise<T> {
    if (!response.ok) {
      let code = `http_${response.status}`;
      let message = response.statusText || '';
      let details: unknown;
      try {
        const data = await response.json();
        if (data?.error) {
          code = data.error.code ?? code;
          message = data.error.message ?? message;
          details = data.error.details;
        } else if (data?.detail) {
          message = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        }
      } catch {
        /* тело не JSON */
      }
      if (response.status === 401 && options.auth !== false) this.onAuthLost?.();
      throw new ApiError(response.status, code, message, details);
    }
    if (options.raw) return response as unknown as T;
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    if (!text) return undefined as T;
    return unwrap<T>(JSON.parse(text));
  }
}

export const tokenStore = new MemoryTokenStore();
export const api = new ApiClient('/api/v1', tokenStore);
